import { useEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { type Worklog } from '@forecastify/shared';
import { anchorTo, Icon, Tooltip, useAnchorRect } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE TIMESHEET CELL, OPENED — round 5 item 32, second pass.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Belekattintva a mezőbe a kis modal ami mutatja a felvett sorokat."* A cell is a task and a day;
 * what it draws is a SUM, and a cell holding more than one entry was read-only with nothing behind
 * the click.
 *
 * ⚠️⚠️ **THE FIRST PASS BUILT A CENTRED `Dialog` AND THAT WAS THE WRONG SHAPE.** *"I asked for
 * this on időlogok/timesheet too"*, with the planner's cell beside it. The report did not ask for
 * a modal in general — it asked for **the one the resource planner has had since C41**, and
 * `allocation-cell.tsx` is that component. A centred sheet over a 92px cell loses the one thing a
 * pop-over gives: the grid stays visible, and the cell it belongs to is still under it.
 *
 * So this is `allocation-cell.tsx`'s shape, down to the numbers, because two pop-overs over two
 * grids that behave differently is two things to learn:
 *
 * | Thing          | Here and there                                               |
 * | -------------- | ------------------------------------------------------------ |
 * | surface        | `w-[320px] rounded-lg border p-3`, `bg-popover shadow-pop`   |
 * | anchoring      | `anchorTo(rect, { side: 'bottom', align: 'start' })`         |
 * | header         | title `12.5px font-medium`, subtitle `11px` joined with ` · ` |
 * | an entry's hours | a BORDERLESS `h-7 w-[78px]` input — box on hover and focus  |
 * | the total      | only at two entries or more                                  |
 * | adding         | a `text-primary` row with a `circle-plus`, at the foot        |
 * | dismissing     | Escape, or a click outside                                   |
 *
 * ⚠️ **What differs is what a worklog IS, and only that.** A booking carries overtime, a
 * distribution and a booking kind, so the planner draws three icon buttons; an hour carries a NOTE
 * — the one thing that tells two entries on one task-day apart — so the note takes that space as
 * an inline field, and the overtime claim keeps its own button beside the delete.
 */
export function CellPopover({
  anchor,
  taskTitle,
  taskKey,
  dayLabel,
  entries,
  canEdit,
  canLog,
  lock = { edit: null, remove: null },
  onEditHours,
  onEditNote,
  onDelete,
  onMarkOvertime,
  onAdd,
  onClose,
}: {
  /**
   * 30 Oct 2026 · why this day's entries cannot be edited or removed (`entry-lock.ts`) — the month
   * lock and the backdating window, which the server enforces and the pop-over used to ignore.
   */
  readonly lock?: { readonly edit: string | null; readonly remove: string | null };
  /** The cell's own control, which is what the pop-over hangs off. */
  readonly anchor: RefObject<HTMLElement | null>;
  readonly taskTitle: string;
  readonly taskKey: string;
  /** Already formatted for a reader — the cell's own column label. */
  readonly dayLabel: string;
  /** Every entry on this task and this day, in the order the grid holds them. */
  readonly entries: readonly Worklog[];
  readonly canEdit: boolean;
  readonly canLog: boolean;
  readonly onEditHours: (entry: Worklog, value: string) => void;
  readonly onEditNote: (entry: Worklog, value: string) => void;
  readonly onDelete: (entry: Worklog) => void;
  readonly onMarkOvertime: (entry: Worklog) => void;
  /** A brand-new entry on this cell's task and day. */
  readonly onAdd: (hours: string, note: string) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const { fmt, parse } = useDuration();
  const { rect } = useAnchorRect(true, anchor);
  const surface = useRef<HTMLDivElement | null>(null);

  /**
   * ⚠️ **The draft row is LOCAL, and it is why there is no always-present empty row.** The first
   * pass drew a blank hours box and a blank note under the entries at all times, so a cell with
   * one entry looked like a cell with two. The planner adds a row when somebody asks for one; this
   * does the same, and the row commits itself on blur rather than behind a button.
   */
  const [draft, setDraft] = useState<{ hours: string; note: string } | null>(
    /**
     * ┌──────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ AN EMPTY CELL OPENS WITH ITS ROW ALREADY THERE.                             │
     * └──────────────────────────────────────────────────────────────────────┘
     *
     * The cell stopped being an input when the pop-over took over the click, and *click a cell,
     * type 8, press Enter* is the thing a timesheet is FOR. A pop-over that opened empty and
     * asked for a second click before anything could be typed would have traded that away for
     * the multi-entry case.
     *
     * ⚠️ So an empty cell opens holding a draft row with the caret in it, and a filled one
     * focuses the entry it already has — see `autoFocus` on the first `Entry`. Three actions
     * either way, and the cases the grid could never express are reachable for the first time.
     */
    entries.length === 0 && canLog ? { hours: '', note: '' } : null,
  );

  /* ⚠️ Escape closes and a click outside closes — the same requirement, for the reason
     `allocation-cell.tsx` gives: in a grid of a hundred cells, opening the wrong one is most of
     the time somebody opens one. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onDown = (event: MouseEvent): void => {
      const node = event.target;
      if (!(node instanceof Node)) return;
      if (surface.current?.contains(node) === true) return;
      if (anchor.current?.contains(node) === true) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [anchor, onClose]);

  if (typeof document === 'undefined') return null;

  const total = entries.reduce((sum, entry) => sum + Number(entry.hours), 0);

  const commitDraft = (): void => {
    if (draft === null) return;
    const parsed = draft.hours.trim() === '' ? null : parse(draft.hours);
    setDraft(null);
    /* ⚠️ A row somebody opened and left blank is not an entry of zero hours. */
    if (parsed === null || parsed === 0) return;
    /* ⚠️ The TYPING goes up — `logs-route.tsx` turns it into hours at the one write boundary. */
    onAdd(draft.hours, draft.note);
  };

  return createPortal(
    <div
      ref={surface}
      role="dialog"
      aria-label={t('logs.timesheet.cellEntries', { task: taskTitle, day: dayLabel })}
      className="bg-popover text-popover-foreground shadow-pop border-border w-[320px] rounded-lg border p-3"
      style={anchorTo(rect, { side: 'bottom', align: 'start', width: 320 })}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[12.5px] font-medium">{taskTitle}</div>
          <div className="text-muted-foreground truncate text-[11px]">
            {[taskKey, dayLabel].filter((one) => one !== '').join(' · ')}
          </div>
        </div>
        <button
          type="button"
          aria-label={t('common.close')}
          className="text-muted-foreground hover:text-foreground -mt-0.5 shrink-0"
          onClick={onClose}
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <div className="mt-2 flex flex-col gap-1.5">
        {entries.map((entry, index) => (
          <Entry
            key={entry.id}
            entry={entry}
            /* ⚠️ The FIRST one only, and only when it is alone: with two entries there is no
               obvious one to mean, and stealing the caret into one of them would be a guess. */
            autoFocus={index === 0 && entries.length === 1}
            canEdit={canEdit}
            canLog={canLog}
            lock={lock}
            onEditHours={onEditHours}
            onEditNote={onEditNote}
            onDelete={onDelete}
            onMarkOvertime={onMarkOvertime}
          />
        ))}

        {draft === null ? null : (
          <div className="flex items-center gap-1">
            <Hours
              value={draft.hours}
              disabled={false}
              label={t('time.entry.hours')}
              autoFocus
              onChange={(value) => {
                setDraft({ ...draft, hours: value });
              }}
              onCommit={commitDraft}
            />
            <Note
              value={draft.note}
              disabled={false}
              placeholder={t('time.log.note')}
              onChange={(value) => {
                setDraft({ ...draft, note: value });
              }}
              onCommit={commitDraft}
            />
            <IconButton
              name="x"
              label={t('common.cancel')}
              disabled={false}
              onClick={() => {
                setDraft(null);
              }}
            />
          </div>
        )}
      </div>

      {/* ⚠️ The total, and only at two entries or more — with one it restates the field directly
          above it, which is a figure that teaches the reader to stop reading. */}
      {entries.length < 2 ? null : (
        <div className="text-muted-foreground mt-2 flex items-center justify-between border-t pt-2 text-[11px]">
          <span>{t('logs.cell.total')}</span>
          <span className="text-foreground font-medium tabular-nums">{fmt(total)}</span>
        </div>
      )}

      {/* 30 Oct 2026 · a locked day says why, where the add would be — it is also the reason the
          fields above are greyed. */}
      {lock.edit === null || (!canLog && !canEdit) ? null : (
        <p className="text-muted-foreground mt-2 border-t pt-2 text-[11.5px] leading-relaxed">
          {lock.edit}
        </p>
      )}

      {!canLog || draft !== null || lock.edit !== null ? null : (
        <button
          type="button"
          className="text-primary hover:bg-accent mt-2 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-[11.5px]"
          onClick={() => {
            setDraft({ hours: '', note: '' });
          }}
        >
          <Icon name="circle-plus" size={13} />
          {t('logs.cell.add')}
        </button>
      )}
    </div>,
    document.body,
  );
}

/** One entry: the hours that belong to the day, the note that tells it from its neighbour. */
function Entry({
  entry,
  autoFocus,
  canEdit,
  canLog,
  lock,
  onEditHours,
  onEditNote,
  onDelete,
  onMarkOvertime,
}: {
  readonly entry: Worklog;
  readonly autoFocus: boolean;
  readonly canEdit: boolean;
  readonly canLog: boolean;
  readonly lock: { readonly edit: string | null; readonly remove: string | null };
  readonly onEditHours: (entry: Worklog, value: string) => void;
  readonly onEditNote: (entry: Worklog, value: string) => void;
  readonly onDelete: (entry: Worklog) => void;
  readonly onMarkOvertime: (entry: Worklog) => void;
}) {
  const t = useT();
  const { fmtNum } = useDuration();
  const stored = fmtNum(entry.hours);
  const [hours, setHours] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /**
   * ┌────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ AN APPROVED CLAIM FREEZES ITS HOUR.                                    │
   * └────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **AND THE SERVER IS THE ONE THAT ENFORCES IT.** `worklog.service.ts` throws
   * `OvertimeApprovedError` from both `update` and `remove`, because this line is UX: it stops
   * somebody typing into a field that will refuse them, and says why on the way past. A screen
   * that only greyed the box would be a lock anybody could pick with a second tab.
   */
  const settled = entry.overtime?.status === 'approved';
  const locked = !canEdit || settled || lock.edit !== null;
  /* 30 Oct 2026 · the delete has its own, narrower rule: only the month lock refuses it. */
  const removeRefused = !canEdit || settled || lock.remove !== null;
  const removeReason = settled ? t('time.error.overtimeApproved') : lock.remove;

  return (
    <div className="flex items-center gap-1">
      <Hours
        value={hours ?? stored}
        disabled={locked}
        label={t('time.entry.hours')}
        autoFocus={autoFocus}
        onFocus={() => {
          setHours(Number(entry.hours) === 0 ? '' : stored);
        }}
        onChange={setHours}
        onCommit={() => {
          const draft = hours;
          setHours(null);
          /* ⚠️ Unchanged is not an edit — a write here would bump a version and audit a change
             nobody made, the same guard the grid's own cells carry. */
          if (draft === null || draft === stored) return;
          onEditHours(entry, draft);
        }}
        onAbandon={() => {
          setHours(null);
        }}
      />
      <Note
        value={note ?? entry.note ?? ''}
        disabled={locked}
        placeholder={t('time.log.note')}
        onChange={setNote}
        onCommit={() => {
          const draft = note;
          setNote(null);
          if (draft === null || draft === (entry.note ?? '')) return;
          onEditNote(entry, draft);
        }}
        onAbandon={() => {
          setNote(null);
        }}
      />

      {/**
       * ⚠️⚠️ **THE OVERTIME CLAIM MOVED IN HERE FROM THE CELL MENU.** It used to be the one item
       * in the cell's dropdown, applied to *every unclaimed entry in the cell* because a menu
       * hanging off a cell has no way to name one of them. P3-5 puts the claim on the HOUR, so a
       * morning session and an afternoon one are two claims — and this is the first surface that
       * can say which.
       */}
      {!canLog && entry.overtime === null ? null : (
        <Overtime entry={entry} onMark={onMarkOvertime} />
      )}
      {/* ⚠️ Drawn and refusing rather than absent, for the reason the overtime button beside it
          gives: a control that vanishes leaves the reader asking where it went. Its tooltip is
          the one that answers *"why can't I delete this?"*. */}
      <IconButton
        name="trash"
        label={t('time.entry.remove')}
        {...(removeReason === null ? {} : { detail: removeReason })}
        disabled={!canEdit && removeReason === null}
        {...(removeRefused
          ? {}
          : {
              onClick: () => {
                onDelete(entry);
              },
            })}
      />
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE CLAIM'S STATE, IN THE BUTTON THAT MADE IT.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Once a task has been marked overtime, the button disappears. This should show the icon in a
 * different state if the request is pending, and another different one if the request was
 * approved/rejected."*
 *
 * ⚠️⚠️ **HIDING IT WAS THE OLD RULE AND IT ANSWERED THE WRONG QUESTION.** The reasoning was the
 * row menu's: a second press answering *already claimed* is a refusal for something the screen
 * already knew. True — and it made the screen forget something it also knew, because a control
 * that vanishes leaves *"did I mark that?"* unanswerable without going to another screen. A press
 * that would be refused is a control that should not be PRESSABLE; that is not the same as one
 * that should not be DRAWN.
 *
 * ⚠️ **The tooltip carries what the icon cannot.** A colour separates four states at a glance and
 * names none of them, so the hover gives the word — and for an approved claim the type and the
 * frozen multiplier with it, which is the number the hour is actually worth.
 *
 * ⚠️ **Not `disabled`, deliberately.** A disabled button is unreachable by keyboard and, in most
 * browsers, emits no pointer events at all — so the one thing this state exists to do, explaining
 * itself on hover, would be the thing it could not do. `aria-disabled` with no handler says the
 * same to a screen reader and keeps the control focusable.
 */
function Overtime({
  entry,
  onMark,
}: {
  readonly entry: Worklog;
  readonly onMark: (entry: Worklog) => void;
}) {
  const t = useT();
  /* ⚠️ 27 Oct 2026 · a WITHDRAWN claim is an ordinary hour again, and raising it revives the
     same claim (`claims a withdrawn hour AGAIN`) — so it offers the mark, not a verdict. */
  const claim = entry.overtime?.status === 'revoked' ? null : entry.overtime;

  if (claim === null) {
    return (
      <IconButton
        name="timer"
        tone="muted"
        label={t('logs.row.markOvertime')}
        onClick={() => {
          onMark(entry);
        }}
      />
    );
  }

  /**
   * ⚠️ **One icon per verdict, so the two decided states do not read as the same thing in two
   * colours** — a reader scanning a column sees the SHAPE before the hue, and a colour alone
   * says nothing to anybody who cannot separate the two.
   *
   * ⚠️⚠️ **`x` RATHER THAN `circle-x`, AND THAT IS THE CLOSED SET WINNING AN ARGUMENT.**
   * `circle-x` is the obvious partner to `circle-check` and lucide ships it — but `icon.tsx`
   * sets one bar for `ICON_ADDITIONS`: *the mockup draws this glyph and the design system does
   * not ship it.* The mockup draws no rejected-overtime icon, so this does not clear it, and
   * adding a glyph because it would look tidier is exactly how a closed set stops being closed.
   */
  const look =
    claim.status === 'approved'
      ? { name: 'circle-check' as const, tone: 'ok' as const, word: t('leave.status.approved') }
      : claim.status === 'rejected'
        ? { name: 'x' as const, tone: 'bad' as const, word: t('leave.status.rejected') }
        : { name: 'timer' as const, tone: 'wait' as const, word: t('leave.status.pending') };

  /* ⚠️ The rate only once there IS one — `0147` leaves a pending claim unpriced, and a figure
     beside a claim nobody has decided is a price the approver has not agreed to. */
  const detail =
    claim.typeName === null
      ? look.word
      : `${look.word} · ${t('resource.item.overtime', {
          name: claim.typeName,
          multiplier: claim.multiplier ?? '1',
        })}`;

  return (
    <IconButton name={look.name} tone={look.tone} label={t('logs.cell.overtime')} detail={detail} />
  );
}

/**
 * ⚠️⚠️ **BORDERLESS AT REST — the standing rule, and `allocation-cell.tsx`'s own class list.** A
 * figure in a pop-over is mostly READ; a permanent box around every number turns it into a form
 * nobody asked to fill in. The box arrives on hover, the full ring on focus.
 */
function Hours({
  value,
  disabled,
  label,
  autoFocus = false,
  onFocus,
  onChange,
  onCommit,
  onAbandon,
}: {
  readonly value: string;
  readonly disabled: boolean;
  readonly label: string;
  readonly autoFocus?: boolean;
  readonly onFocus?: () => void;
  readonly onChange: (value: string) => void;
  readonly onCommit: () => void;
  readonly onAbandon?: () => void;
}) {
  const t = useT();
  const { fmtNum } = useDuration();
  return (
    <Tooltip asChild content={t('time.hours.help')}>
      <input
        type="text"
        disabled={disabled}
        value={value}
        /* ⚠️ The row did not exist until somebody pressed *add*, so the caret belongs in the field
         they asked for — an autofocus that follows an explicit request, not one on page load. */
        autoFocus={autoFocus}
        aria-label={label}
        placeholder={fmtNum(0)}
        className="focus:border-input focus:bg-background hover:enabled:border-input h-7 w-[78px] min-w-0 rounded-md border border-transparent bg-transparent px-2 text-right text-[12px] tabular-nums outline-none disabled:cursor-default"
        onFocus={onFocus}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        onBlur={onCommit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onAbandon?.();
          }
        }}
      />
    </Tooltip>
  );
}

/** The same editor, taking the row's remaining width — a note is prose, not a figure. */
function Note({
  value,
  disabled,
  placeholder,
  onChange,
  onCommit,
  onAbandon,
}: {
  readonly value: string;
  readonly disabled: boolean;
  readonly placeholder: string;
  readonly onChange: (value: string) => void;
  readonly onCommit: () => void;
  readonly onAbandon?: () => void;
}) {
  return (
    <input
      type="text"
      disabled={disabled}
      value={value}
      aria-label={placeholder}
      placeholder={placeholder}
      className="focus:border-input focus:bg-background hover:enabled:border-input h-7 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-[12px] outline-none disabled:cursor-default"
      onChange={(event) => {
        onChange(event.target.value);
      }}
      onBlur={onCommit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onAbandon?.();
        }
      }}
    />
  );
}

/**
 * The planner's own icon button, down to the size and the two-line tooltip — the control's name
 * in bold, its current value under it, which is the shape round 5 item 4 settled.
 */
function IconButton({
  name,
  tone = 'muted',
  label,
  detail,
  disabled = false,
  onClick,
}: {
  readonly name: 'timer' | 'trash' | 'x' | 'circle-check';
  /** ⚠️ Four states and four colours — see `Overtime`. */
  readonly tone?: 'muted' | 'wait' | 'ok' | 'bad';
  readonly label: string;
  /** The second tooltip line: what the control currently SAYS, where that differs from its name. */
  readonly detail?: string;
  readonly disabled?: boolean;
  /** ⚠️ Absent makes it a READING rather than a control — see `Overtime` on why not `disabled`. */
  readonly onClick?: () => void;
}) {
  const colour =
    tone === 'ok'
      ? 'text-primary'
      : tone === 'bad'
        ? 'text-destructive'
        : tone === 'wait'
          ? 'text-amber-600'
          : 'text-muted-foreground';
  return (
    <Tooltip
      width={220}
      content={
        <span className="flex flex-col gap-0.5">
          <span className="font-medium">{label}</span>
          {detail === undefined ? null : <span className="text-muted-foreground">{detail}</span>}
        </span>
      }
    >
      <button
        type="button"
        disabled={disabled}
        aria-disabled={onClick === undefined}
        aria-label={detail === undefined ? label : `${label} · ${detail}`}
        className={`hover:bg-accent flex size-7 shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-40 ${colour} ${
          onClick === undefined ? 'cursor-default' : 'hover:text-foreground'
        }`}
        onClick={onClick}
      >
        <Icon name={name} size={13} />
      </button>
    </Tooltip>
  );
}
